// eslint-disable no-unused-vars
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  IconButton,
  Typography,
  withStyles,
} from '@material-ui/core';
import { ExpandLess, InfoOutlined, Search as SearchIcon } from '@material-ui/icons';
import PropTypes from 'prop-types';
import React, {
  useEffect, useMemo, useState, useCallback,
} from 'react';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import PF2MEquipmentPool from '~/components/PF2MEquipmentPoolV2';
import PF2MEquipmentScheduleModal from '~/components/PF2MEquipmentScheduleModal';
import PF2MFullScreenButton from '~/components/PF2MFullScreenButton';
import ContextMenu, { PF2MMenuItem, useContextMenu } from '~/components/PF2MMenuContext';
import { withModal } from '~/components/PF2MModal';
import PF2MTooltip from '~/components/PF2MTooltip';
import PF2MSearchInput from '~/components/PF2MSearchInput';
import PF2MWarnFeatureFlag from '~/PF2MWarnFeatureFlag';
import usePlatformConfiguration from '~/services/platformConfiguration';
import {
  getDispatchDestinationsElements,
  getDispatchDestinationsTrucks,
  getDispatchOriginsTrucks,
  getSyncStatus,
  getEquipmentStatuses,
  setSelectedLoadAllocation,
  setSelectedTruckAllocation,
  updateTruckAllocations,
  getLoadAllocations,
  getTruckAllocations,
} from '~/store/dispatch/actions';
import { DispatchDragNDropTypes, DispatchStatusNumber } from '~/utils';
import { newMomentDate, calculateDurationBytimestamp } from '~/utils/moment';
import { getCodeFromOrigin, getCodeFromStatus, toCodesMapByKey } from '~/utils/codes';
import { LoadAllocationModal, TruckAllocationModal } from '../../LoadSchedules/AllocationsTab/AllocationModal';
import CollapseEquipmentList from '../CollapseEquipmentList';
import DispatchLabels from '../DispatchLabels';
import styles from '../styles';
import TruckOperators from '../TruckOperatorStatus';
import DestinationTruckModal, { DestinationElementModal } from './DestinationModal';
import DestinationRow from './DestinationRow';
import { getTabletConfiguration } from '~/store/manager/actions';
import {
  getCodes,
  getElements,
  getSubElements,
  getCodeGroups,
  getNrefOperators as getOperators,
  getEquipmentGroups,
  getEquipmentTypes,
  getMaterials,
  getNrefOperatorsGroups,
} from '~/store/nref/actions';
import PF2MEquipmentPoolFilter from '~/components/PF2MEquipmentPoolFilter';

import useRealtimeSignal from '../../useMqtt/useRealtimeSignal';
import useThrottledRealtimeRefresh from '../../useMqtt/useThrottledRealtimeRefresh';


const GENERIC_DESTINATION = {
  id: null,
  name: '',
  id_element: 0,
  active: 1,
};

// No meaning to have validation for one line temporary toggle feature
// eslint-disable-next-line react/destructuring-assignment, react/prop-types
const OffComp = props => <div>{props.children}</div>;

// const TRUCK_TYPE_ID = 2;

const EquipmentScheduleModal = withModal(PF2MEquipmentScheduleModal);

const DestinationsTabV2 = ({ classes }) => {
  const rootRef = React.useRef(0);

  const dispatch = useDispatch();
  const handleFS = useFullScreenHandle();

  const { t: translate } = useTranslation();

  const syncStatus = useSelector(state => state.dispatch.syncStatus);
  const truckOrigins = useSelector(state => (state.dispatch.truckOrigins || []));
  const destinationRows = useSelector(state => state.dispatch.elementDestinations);
  const equipmentStatuses = useSelector(state => state.dispatch.equipmentStatuses);
  const equipmentsSchedules = useSelector(state => state.dispatch.equipmentsSchedules);

  const codes = useSelector(state => state.nref.codes);
  const elements = useSelector(state => state.nref.elements);
  const materials = useSelector(state => state.nref.materials);
  const operators = useSelector(state => state.nref.operators);
  const codeGroups = useSelector(state => state.nref.codeGroups);
  const subelements = useSelector(state => state.nref.subElements);
  const operatorsGroups = useSelector(state => state.nref.operatorsGroups);
  const equipmentGroups = useSelector(state => state.nref.equipmentGroups)
    .filter(r => [2].includes(r.id_equipament));
  const equipmentTypes = useSelector(state => state.nref.equipmentTypes)
    .filter(r => [1, 2, 15, 29].includes(r.id));

  const [expanded, setExpanded] = useState(true);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [filterTextLoad, setFilterTextLoad] = useState('');
  const [filterTextTruck, setFilterTextTruck] = useState('');
  const [filterTextOrigin, setFilterTextOrigin] = useState('');
  const [loadAllocationModalData, setLoadAllocationModalData] = useState();
  const [truckAllocationModalData, setTruckAllocationModalData] = useState();
  const [equipmentScheduleModal, setEquipmentScheduleModal] = useState(false);
  const [isDestinationTruckModalOpen, setIsDestinationTruckModalOpen] = useState(false);
  const [isDestinationElementModalOpen, setIsDestinationElementModalOpen] = useState(false);

  const [loadContextMenuData, openLoadContextMenu, closeLoadContextMenu] = useContextMenu();
  const [truckContextMenuData, openTruckContextMenu, closeTruckContextMenu] = useContextMenu();

  const [poolEquipmentGroup, setPoolEquipmentGroup] = useState([]);
  const [poolEquipmentName, setPoolEquipmentName] = useState('');

  const processDisptachIndicatorsTime = (
    usePlatformConfiguration('process_dispatch_indicators_time')?.value || '0'
  );

  useEffect(() => {
    if (!dataLoaded) {
      dispatch(getCodes());
      dispatch(getElements());
      dispatch(getOperators());
      dispatch(getMaterials());
      dispatch(getCodeGroups());
      dispatch(getSubElements());
      dispatch(getEquipmentTypes());
      dispatch(getEquipmentGroups());
      dispatch(getNrefOperatorsGroups());
      dispatch(getTabletConfiguration());
      dispatch(getSyncStatus());

      setDataLoaded(true);
    }
  });

  const refreshBoard = useCallback(() => {
    dispatch(getLoadAllocations());
    dispatch(getTruckAllocations());
    dispatch(getEquipmentStatuses());
    dispatch(getDispatchOriginsTrucks());
    dispatch(getDispatchDestinationsTrucks());
    dispatch(getDispatchDestinationsElements());
  }, [dispatch]);

  const { signal } = useThrottledRealtimeRefresh({
    refresh: refreshBoard,
    throttleMs: 1000,
  });

  useRealtimeSignal({
    topic: 'DISPATCHUPDATE',
    onSignal: signal,
  });

  const useCreateMemoMapById = (array, key) => useMemo(
    () => Object.fromEntries(array?.map(item => [item[key], item]) || []),
    [array],
  );

  const codesByKey = useMemo(() => toCodesMapByKey(codes), [codes]);
  const elementsById = useCreateMemoMapById(elements, 'id');
  const materialsById = useCreateMemoMapById(materials, 'id');
  // Grupos de código também repetem `id` entre tipos de equipamento.
  const codeGroupsByIdEquipType = useMemo(() => Object.fromEntries(
    codeGroups?.map(group => [`${group.id}:${group.id_equip}`, group]) || [],
  ), [codeGroups]);
  const subelementsById = useCreateMemoMapById(subelements, 'id');
  const operatorById = useCreateMemoMapById(operators, 'id_operator');
  const operatorsGroupById = useCreateMemoMapById(operatorsGroups, 'id');
  const syncStatusByEquipId = useCreateMemoMapById(syncStatus, 'equip_id');
  const statusByEquipId = useCreateMemoMapById(equipmentStatuses, 'id_equip');


  const truckOriginsEquipmentStatus = truckOrigins
    .filter(truck => truck?.equip_id)
    .map((truck) => {
      const truckStatus = statusByEquipId[truck.equip_id];
      const code = getCodeFromStatus(codesByKey, truckStatus)
        ?? getCodeFromOrigin(codesByKey, truck);

      const durations = truckStatus
        ? calculateDurationBytimestamp(truckStatus.start_timestamp)
        : null;

      const codeType = code?.code_type;

      const statusValue = codeType === undefined
        ? truck.status
        : DispatchStatusNumber[codeType];

      // Tipo e grupo seguem o código resolvido (status do equipamento), para não divergirem
      // do código exibido quando `truckOrigins` ainda está com o código anterior.
      const codeTypeId = code?.code_type ?? truck.code_type_id;
      const codeGroupId = code?.id_group ?? truck.code_group_id;
      const codeGroup = codeGroupsByIdEquipType[`${codeGroupId}:${code?.id_equip ?? truck.equip_type_id}`]
        ?? codeGroups?.find(group => group.id === codeGroupId);
      const operator = operatorById[truck?.operator_id];
      const operatorGroup = operatorsGroupById[truck?.operator_group_id];
      const syncStatusByStatus = syncStatusByEquipId[truckStatus?.id_equip];

      const materialByStatus = materialsById[truck?.current_material_id];

      const elementByStatus = elementsById[truck?.current_element_id];
      const subelementByStatus = subelementsById[truck?.current_element_point_id];
      const destinationElementByStatus = elementsById[truck?.current_destination_id];
      const destinationSubelementByStatus = subelementsById[truck?.current_destination_point_id];

      return {
        ...truck,
        durations,
        status: statusValue,
        code_id: code?.id ?? truck.code_id,
        code_type_id: codeTypeId,
        start_timestamp: truckStatus?.start_timestamp,
        destination_point_name: truck.load_allocation_excavator_name,
        operators_name: operator?.name ?? translate('validation:OperatorNotFound'),
        timestamp_sync_request: syncStatusByStatus?.timestamp_sync_request ?? null,
        code_group_name: codeGroup?.name ?? translate('validation:CodeGroupNotFound'),
        current_element_name: elementByStatus?.name ?? translate('validation:ElementNotFound'),
        codes_name: code?.name ?? translate('validation:CodeNotFound'),
        current_material_name: materialByStatus?.name ?? translate('validation:MaterialNotFound'),
        operator_group_name: operatorGroup?.name ?? translate('validation:OperatorGroupNotFound'),
        current_element_point_name: subelementByStatus?.name ?? translate('validation:SubElementNotFound'),
        current_destination_name: destinationElementByStatus?.name ?? translate('validation:ElementNotFound'),
        current_destination_point_name: destinationSubelementByStatus?.name ?? translate('validation:SubElementNotFound'),
      };
    });

  const availablePoolEquips = [
    ...truckOriginsEquipmentStatus
      .map(TruckOperators)
      .filter(st => st.isHealthy && !st.isOnCicle && !st.isOnQuickPause)
      .map(({ to }) => to),
  ];

  const maintenancePoolEquips = [
    ...truckOriginsEquipmentStatus
      .map(TruckOperators)
      .filter(st => !st.isHealthy)
      .map(({ to }) => to),
  ];

  const leftLaneTrucks = truckOriginsEquipmentStatus
    .map(TruckOperators)
    .filter(st => st.isHealthy)
    .filter(st => st.isOnCicle || st.isOnQuickPause)
    .filter(st => st.isLoading)
    .map(({ to }) => to);

  const rightLaneTrucks = truckOriginsEquipmentStatus
    .map(TruckOperators)
    .filter(st => st.isHealthy)
    .filter(st => st.isOnCicle || st.isOnQuickPause)
    .filter(st => !st.isLoading)
    .map(({ to }) => to);


  const equipScheduleModalData = useMemo(() => {
    if (!equipmentsSchedules || !equipmentScheduleModal) {
      return {};
    }
    return equipmentsSchedules
      .find(e => (e.group_equipament_links_id === equipmentScheduleModal.equip_id)) || {
      ...equipmentScheduleModal,
      equipaments_id: equipmentScheduleModal.equip_type_id,
      equipament_groups_id: equipmentScheduleModal.equip_group_id,
      group_equipament_links_id: equipmentScheduleModal.equip_id,
    };
  }, [equipmentScheduleModal, equipmentsSchedules]);

  const allocateTruck = (_e, item) => {
    dispatch(setSelectedTruckAllocation(item));
    setTruckAllocationModalData(true);
  };
  const allocateLoader = (_e, item) => {
    dispatch(setSelectedLoadAllocation(item));
    setLoadAllocationModalData(true);
  };
  const editTruckSchedule = (_e, item) => {
    setEquipmentScheduleModal(item);
  };
  const editLoadSchedule = (_e, item) => {
    setEquipmentScheduleModal(item);
  };

  function getDestinationIdsFromFilteredTrucks(
    trucks,
    filterByText = '',
    filterByOrigin = '',
  ) {
    return trucks
      .filter(
        truck => truck.equip_name
          && truck.equip_name
            .toLowerCase()
            .includes((filterTextTruck || '').toLowerCase())
          && (truck.current_excavator_name || '')
            .toLowerCase()
            .includes(filterByText.toLowerCase())
          && (truck.current_origin_point_name || '')
            .toLowerCase()
            .includes(filterByOrigin.toLowerCase()),
      )
      .map(truck => truck.current_destination_point_id)
      .filter(id => id !== null);
  }

  const leftDestinationIds = getDestinationIdsFromFilteredTrucks(
    leftLaneTrucks, filterTextLoad, filterTextOrigin,
  );
  const rightDestinationIds = getDestinationIdsFromFilteredTrucks(
    rightLaneTrucks, filterTextLoad, filterTextOrigin,
  );

  const allRelevantDestinationIds = [
    ...new Set([...leftDestinationIds, ...rightDestinationIds]),
  ];

  function filterTrucksByDestinationIds(trucks, destinationIds) {
    return trucks.filter(
      truck => destinationIds.includes(truck.current_destination_point_id),
    );
  }

  const filteredLeftLaneTrucks = filterTrucksByDestinationIds(
    leftLaneTrucks,
    allRelevantDestinationIds,
  );

  const filteredRightLaneTrucks = filterTrucksByDestinationIds(
    rightLaneTrucks,
    allRelevantDestinationIds,
  );

  const filterDestinationRows = destinationRows
    .filter(r => allRelevantDestinationIds.includes(r.id));


  const renderTableHeader = () => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px' }}>
      <span className={classes.labelTypeSelector}>{translate('common:LoadEquipment')}</span>
      <span className={classes.labelTypeSelector}>{translate('common:Element')}</span>
    </div>
  );

  const setEquipmentTypes = (e) => {
    setPoolEquipmentGroup(
      equipmentGroups.filter(
        equip => [...e.target.value.map(target => target.value)]
          .includes(equip.id_equipament),
      ).map(eg => eg.id),
    );
  };

  const filters = React.useMemo(() => ([
    {
      type: 'multiSelect',
      label: `${translate('common:EquipmentGroup')}`,
      value: poolEquipmentGroup,
      options: equipmentGroups
        .map(e => ({
          value: e.id,
          label: e.name,
        })),
      onChange: e => setPoolEquipmentGroup(e.target.value.map(r => r.value)),
      placeholder: `${translate('common:Select')}`,
    },
    {
      type: 'textField',
      label: `${translate('common:Equipment')}`,
      value: poolEquipmentName,
      onChange: e => setPoolEquipmentName(e?.target?.value),
      placeholder: `${translate('common:Find')}`,
    },
  ]), [
    poolEquipmentGroup,
    poolEquipmentName,
    equipmentGroups,
  ]);

  const applyFiltersToPoolList = (list, listGroup, equipName) => {
    const listFilteredByGroup = list.filter(
      r => listGroup.length === 0 || listGroup.includes(r.equip_group_id),
    );

    if (!equipName?.trim()) {
      return listFilteredByGroup;
    }

    const search = equipName?.trim()?.toLowerCase();

    const listFilteredByName = listFilteredByGroup.filter(
      r => r.equip_name?.toLowerCase()?.includes(search),
    );

    return listFilteredByName;
  };


  const renderFilterTable = () => (
    <div
      style={{
        display: 'flex',
        width: '100%',
        justifyContent: 'space-between',
        padding: '16px 8px',
        gap: '24px',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: '45px',
        }}
      >
        <PF2MSearchInput
          value={filterTextOrigin}
          onChange={e => setFilterTextOrigin(e.target.value)}
          placeholder={translate('common:Find')}
          text={`${translate('common:SearchFor')} ${translate('common:Origin')}`}
          startIcon={<SearchIcon fontSize={'small'} color="primary" />}
        />
        <PF2MSearchInput
          value={filterTextLoad}
          onChange={e => setFilterTextLoad(e.target.value)}
          placeholder={translate('common:Find')}
          text={`${translate('common:SearchFor')} ${translate('common:LoadEquipment')}`}
          startIcon={<SearchIcon fontSize={'small'} color="primary" />}
        />
        <PF2MSearchInput
          value={filterTextTruck}
          onChange={e => setFilterTextTruck(e.target.value)}
          placeholder={translate('common:Find')}
          text={`${translate('common:SearchFor')} ${translate('common:TruckEquipment')}`}
          startIcon={<SearchIcon fontSize={'small'} color="primary" />}
        />
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <PF2MTooltip
          title={<DispatchLabels />}
          PopperProps={{ disablePortal: true }}
        >
          <IconButton className={classes.iconButton}>
            <InfoOutlined fontSize="small" />
          </IconButton>
        </PF2MTooltip>
        <PF2MFullScreenButton
          isFullScreenActive={handleFS.active}
          open={handleFS.enter}
          close={handleFS.exit}
          variant="outlined"
        />
      </div>
    </div>
  );
  const renderGenericRow = () => {
    const workingDestinations = destinationRows.map(r => r.id);
    const leftLane = leftLaneTrucks
      .filter(r => !workingDestinations.includes(r.current_destination_point_id));
    const rightLane = rightLaneTrucks
      .filter(r => !workingDestinations.includes(r.current_destination_point_id));

    if (!leftLane.length && !rightLane.length) return null;
    return (
      <DestinationRow
        destination={GENERIC_DESTINATION}
        leftLane={leftLane}
        rightLane={rightLane}
        isGenericRow
        destinations={destinationRows}
        openTruckModal={setIsDestinationTruckModalOpen}
        openElementModal={setIsDestinationElementModalOpen}
        onTruckContextMenu={openTruckContextMenu}
      />
    );
  };

  const shouldShowRow = (destinationRow, allLaneTrucks) => {
    if (destinationRow.is_programmed) return true;
    return allLaneTrucks.some(r => r.current_destination_point_id === destinationRow.id);
  };

  const renderRows = () => (
    <div style={{
      width: '100%',
      flex: 1,
      overflowY: 'scroll',
    }}
    >
      {renderFilterTable()}
      {renderTableHeader()}
      <div style={{
        borderTop: '1px solid rgba(224, 224, 224, 1)',
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
      }}
      >
        <>
          {filterDestinationRows
            .filter(r => shouldShowRow(r, [
              ...filteredLeftLaneTrucks,
              ...filteredRightLaneTrucks,
            ]))
            .map(r => (
              <DestinationRow
                key={r.id}
                destination={r}
                leftLane={filteredLeftLaneTrucks
                  .filter(x => x.current_destination_point_id === r.id)}
                rightLane={filteredRightLaneTrucks
                  .filter(x => x.current_destination_point_id === r.id)}
                destinations={destinationRows}
                onTruckDrop={(item, destinationId) => (
                  dispatch(updateTruckAllocations({
                    item,
                    key: 'destination_point_id',
                    value: destinationId,
                    refresh: false,
                    skipLoading: true,
                  }))
                )}
                openTruckModal={setIsDestinationTruckModalOpen}
                openElementModal={setIsDestinationElementModalOpen}
                onTruckContextMenu={openTruckContextMenu}
              />
            ))
          }
          {renderGenericRow()}
        </>
      </div>
    </div>
  );

  const renderPools = () => (
    <Accordion
      defaultExpanded
      onChange={() => setExpanded(!expanded)}
      expanded={expanded}
    >
      <AccordionSummary
        expandIcon={<ExpandLess />}
        id="1"
      >
        <Typography>{translate('common:AvailableEquipments')}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <div style={{ display: 'flex', width: '100%' }}>
          <PF2MEquipmentPoolFilter filters={filters} />
          <div style={{
            display: 'flex',
            marginTop: 10,
            width: '100%',
          }}
          >
            <PF2MEquipmentPool
              equips={
                applyFiltersToPoolList(
                  availablePoolEquips, poolEquipmentGroup, poolEquipmentName,
                )
              }
              destinations={destinationRows}
              onEquipClick={() => null}
              onItemDropped={() => null}
              headerText={translate('common:AvailableEquipments').toUpperCase()}
              truckItemType={DispatchDragNDropTypes.POOL_TRUCK}
              acceptDrop={false}
              extraInfoKey={'destination_point_name'}
              onContextMenu={{
                truck: openTruckContextMenu,
                loader: openLoadContextMenu,
              }}
              operationTypeEffective
            />
            <PF2MEquipmentPool
              equips={
                applyFiltersToPoolList(
                  maintenancePoolEquips, poolEquipmentGroup, poolEquipmentName,
                )
              }
              destinations={destinationRows}
              onEquipClick={() => null}
              onItemDropped={() => null}
              headerText={translate('common:MaintenanceEquipments').toUpperCase()}
              customStyles={{ marginLeft: 10 }}
              truckItemType={DispatchDragNDropTypes.POOL_TRUCK}
              acceptDrop={false}
              extraInfoKey={'destination_point_name'}
              onContextMenu={{
                truck: openTruckContextMenu,
                loader: openLoadContextMenu,
              }}
              operationTypeEffective
            />
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );

  const children = (
    <div className={classes.tabContainer}>
      <FullScreen handle={handleFS}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            height: handleFS.active ? '95%' : '82vh',
            backgroundColor: '#ffffff',
          }}
        >
          {renderRows()}
          {renderPools()}
        </div>

        <div ref={rootRef}>
          <DestinationTruckModal
            isOpen={isDestinationTruckModalOpen}
            close={() => setIsDestinationTruckModalOpen(false)}
            container={() => rootRef.current}
          />

          <DestinationElementModal
            isOpen={isDestinationElementModalOpen}
            close={() => setIsDestinationElementModalOpen(false)}
            container={() => rootRef.current}
          />

          <ContextMenu
            {...truckContextMenuData}
            close={closeTruckContextMenu}
            container={() => rootRef.current}
          >
            <PF2MMenuItem onClick={allocateTruck}>
              {translate('common:AddEditAllocation')}
            </PF2MMenuItem>
            <PF2MMenuItem onClick={editTruckSchedule}>
              {translate('common:EditEquipmentScheduling')}
            </PF2MMenuItem>
          </ContextMenu>

          <LoadAllocationModal
            isOpen={loadAllocationModalData}
            close={() => setLoadAllocationModalData(null)}
            container={() => rootRef.current}
          />

          <ContextMenu
            {...loadContextMenuData}
            close={closeLoadContextMenu}
            container={() => rootRef.current}
          >
            <PF2MMenuItem onClick={allocateLoader}>
              {translate('common:AddEditAllocation')}
            </PF2MMenuItem>
            <PF2MMenuItem onClick={editLoadSchedule}>
              {translate('common:EditEquipmentScheduling')}
            </PF2MMenuItem>
          </ContextMenu>

          <TruckAllocationModal
            modalData={truckAllocationModalData}
            closeModal={() => setTruckAllocationModalData(null)}
            container={() => rootRef.current}
          />

          {
            equipScheduleModalData && (
              <EquipmentScheduleModal
                container={() => rootRef.current}
                modalData={equipmentScheduleModal}
                closeModal={() => setEquipmentScheduleModal(false)}
                settings={{
                  skipBulkUpdate: true,
                }}
                fixed={{
                  equipmentType: equipScheduleModalData.equipaments_id || 0,
                  equipmentGroup: equipScheduleModalData.equipament_groups_id || 0,
                  equipment: equipScheduleModalData.group_equipament_links_id || 0,
                  startTime: newMomentDate(),
                  duration: '00:30:00',
                  element: equipScheduleModalData.flowchart_element_id || 0,
                  subElement: equipScheduleModalData.flowchart_element_point_id || 0,
                }}
              />
            )
          }
        </div>

      </FullScreen>
    </div>
  );

  return (
    <PF2MWarnFeatureFlag
      flag={processDisptachIndicatorsTime >= 1}
      featureName="DispatchIndicators (process_dispatch_indicators_time >= 1)"
      OffComponent={OffComp}
      OnComponent={CollapseEquipmentList}
      props={{
        classes: { container: classes.tabContainer },
        children,
      }}
    />
  );
};

DestinationsTabV2.propTypes = {
  classes: PropTypes.object.isRequired,
};

DestinationsTabV2.defaultProps = {};

export default withStyles(styles)(DestinationsTabV2);
